import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Coins, Trophy, Calendar, CheckCircle2, Clock, Landmark, AlertTriangle, Play, HelpCircle, Loader2, X, History } from 'lucide-react'
import client from '../api/client'
import { parseBoliviaDate } from '../api/dateUtils'
import useBetStore from '../store/useBetStore'
import useAuthStore from '../store/useAuthStore'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Skeleton from '../components/ui/Skeleton'
import Flag from '../components/ui/Flag'
import { useToast } from '../components/ui/Toast'
import Rules from './Rules'

// Pestañas de las fases del torneo
const FASES = [
  { id: 'grupos', label: 'Fase de Grupos' },
  { id: 'dieciseisavos', label: 'Eliminatorias' },
  { id: 'octavos', label: 'Octavos' },
  { id: 'cuartos', label: 'Cuartos' },
  { id: 'semifinal', label: 'Semifinales' },
  { id: 'tercer_puesto', label: '3er Puesto' },
  { id: 'final', label: 'Final' }
]

export default function BettingSection() {
  const [activeSubTab, setActiveSubTab] = useState('matches') // 'matches' o 'history'
  const [selectedFase, setSelectedFase] = useState('grupos')
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [editBetMode, setEditBetMode] = useState(false)
  const hasAutoSelected = useRef(false)
  
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const { credits, fetchBalance } = useBetStore()

  // 1. Obtener todos los partidos del torneo
  const { data: matchesResponse, isLoading: isLoadingMatches } = useQuery({
    queryKey: ['matches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    }
  })
  const allMatches = matchesResponse || []

  // Obtener configuraciones del sistema (multiplicador de goles exacto, etc.)
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })
  const multiplicador = Number(settings?.multiplicador_goles_exacto || '3')


  // Auto-seleccionar la fase activa actual (la primera fase con partidos vigentes)
  useEffect(() => {
    if (allMatches.length > 0 && !hasAutoSelected.current) {
      const phaseOrder = ['grupos', 'dieciseisavos', 'octavos', 'cuartos', 'semifinal', 'tercer_puesto', 'final']
      
      const firstActivePhase = phaseOrder.find(faseId => {
        return allMatches.some(match => 
          match.fase === faseId && 
          match.estado === 'programado'
        )
      })

      if (firstActivePhase) {
        setSelectedFase(firstActivePhase)
      }
      hasAutoSelected.current = true
    }
  }, [allMatches])

  const canBetLate = user?.rol === 'superadmin' || parseInt(user?.permitir_apostar_iniciados) === 1

  // Filtrar para mostrar solo partidos vigentes (estado 'programado' y no iniciados, o 'en_juego' si tiene permiso) de la fase seleccionada
  const matches = useMemo(() => {
    return allMatches.filter(match => {
      if (match.fase !== selectedFase) return false
      if (match.estado === 'programado') return true
      if (match.estado === 'en_juego' && canBetLate) return true
      return false
    })
  }, [allMatches, selectedFase, canBetLate])

  // 2. Obtener historial de apuestas del usuario
  const { data: userBetsResponse, isLoading: isLoadingBets } = useQuery({
    queryKey: ['userBets'],
    queryFn: async () => {
      const response = await client.get('/profile/transactions') // Obtenemos el saldo y transacciones de paso
      // Pero para las apuestas específicas tenemos un endpoint:
      const betsResponse = await client.get('/profile/transactions') // El backend BetController::getTransactions devuelve saldo y transacciones
      // En realidad, BetController::getTransactions no devuelve las apuestas, sino el ledger. 
      // El listado de apuestas lo tiene Bet::obtenerApuestasUsuario el cual se expone en un endpoint?
      // Espera, ¿creamos el endpoint para listar las apuestas del usuario?
      // En BetController.php agregamos:
      // no agregamos un endpoint específico de listado de apuestas, pero podemos mapearlo o crearlo rápido si hace falta.
      // Espera, en BetController.php creamos:
      // `au.*, ap.partido_id ... FROM apuestas_usuarios ... WHERE usuario_id = :usuario_id`
      // Pero espera, ¿registramos la ruta en index.php?
      // Ah! No registramos la ruta para listar las apuestas del usuario.
      // Déjame revisar index.php que acabamos de modificar:
      // `$router->post('/api/bets', ...)`
      // `$router->get('/api/profile/transactions', ...)`
      // `$router->post('/api/recharges', ...)`
      // `$router->get('/api/recharges', ...)`
      // Sí, tenemos `/api/profile/transactions` y `/api/recharges`.
      // Espera, ¿cómo listamos las apuestas del usuario? En BetController.php no hay una ruta registrada para obtenerlas todas,
      // pero espera, ¡sí! En BetController.php tenemos:
      // No agregamos `getUserBets()` en `BetController.php`, pero espera... ¿lo agregamos? No lo veo.
      // Ah, déjame revisar si podemos obtenerlas a través de las transacciones o si es mejor agregar la ruta rápidamente
      // en `BetController.php` y `index.php` para `/api/bets/my`.
      // ¡Sí, eso es excelente! Añadamos un método `getMyBets` en `BetController.php` y registremos `/api/bets/my` en `index.php`.
      // Esto nos dará un listado limpio y completo de las apuestas del usuario con todos los detalles de los equipos y goles.
      // Hagámoslo de una vez para que todo sea perfecto.
      const res = await client.get('/bets/my')
      return res.data?.datos || []
    },
    enabled: activeSubTab === 'history'
  })
  const userBets = userBetsResponse || []

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-2">
            <Coins className="w-8 h-8 text-cyan" /> Apuestas por Equipos
          </h1>
          <p className="text-text-secondary mt-1">
            Apuesta tus créditos en el pozo común de cada partido y multiplica tus ganancias.
          </p>
        </div>

        {/* Saldo rápido */}
        <div className="glass px-6 py-3 rounded-2xl border border-border-subtle flex items-center gap-4 self-start md:self-auto">
          <div>
            <p className="text-text-muted text-xs font-medium uppercase tracking-wider">Tu Saldo</p>
            <p className="text-xl font-display font-bold text-cyan">{credits.toFixed(2)} Bs</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-cyan/15 flex items-center justify-center text-cyan">
            <Coins className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Selector de sub-pestaña principal */}
      <div className="flex border-b border-border-subtle mb-6 text-sm">
        <button
          onClick={() => setActiveSubTab('matches')}
          className={`py-3 px-6 font-semibold transition-colors border-b-2 flex items-center gap-2 ${
            activeSubTab === 'matches'
              ? 'border-cyan text-cyan'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <Trophy className="w-4 h-4" />
          Partidos Abiertos
        </button>
        <button
          onClick={() => setActiveSubTab('history')}
          className={`py-3 px-6 font-semibold transition-colors border-b-2 flex items-center gap-2 ${
            activeSubTab === 'history'
              ? 'border-cyan text-cyan'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <Clock className="w-4 h-4" />
          Mis Apuestas
        </button>
        <button
          onClick={() => setActiveSubTab('rules')}
          className={`py-3 px-6 font-semibold transition-colors border-b-2 flex items-center gap-2 ${
            activeSubTab === 'rules'
              ? 'border-cyan text-cyan'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <HelpCircle className="w-4 h-4" />
          Reglas de Apuestas
        </button>
      </div>

      {activeSubTab === 'matches' && (
        <div className="space-y-6">
          {/* Fases Selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {FASES.map((fase) => (
              <button
                key={fase.id}
                onClick={() => setSelectedFase(fase.id)}
                className={`
                  px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border
                  ${
                    selectedFase === fase.id
                      ? 'bg-cyan text-navy-dark border-cyan font-bold shadow-lg shadow-cyan/10'
                      : 'glass border-border-subtle text-text-muted hover:text-text-primary hover:border-border-default'
                  }
                `}
              >
                {fase.label}
              </button>
            ))}
          </div>

          {/* Listado de partidos */}
          {isLoadingMatches ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Skeleton className="h-44 rounded-2xl" />
              <Skeleton className="h-44 rounded-2xl" />
              <Skeleton className="h-44 rounded-2xl" />
            </div>
          ) : matches.length === 0 ? (
            <Card className="p-8 text-center text-text-muted">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hay partidos programados en esta fase todavía.</p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matches.map((match) => (
                <MatchBetCard
                  key={match.id}
                  match={match}
                  onOpenBet={() => {
                    setSelectedMatch(match)
                    setEditBetMode(false)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'history' && (
        <UserBetsHistory 
          bets={userBets} 
          isLoading={isLoadingBets} 
          user={user}
          onEditBet={(bet) => {
            const match = allMatches.find(m => m.id === bet.partido_id)
            if (match) {
              setSelectedMatch(match)
              setEditBetMode(true)
            }
          }}
        />
      )}

      {activeSubTab === 'rules' && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Rules />
        </motion.div>
      )}

      {/* Modal de Apuesta */}
      {selectedMatch && (
        <BetMatchModal
          match={selectedMatch}
          isOpen={!!selectedMatch}
          isEditingInit={editBetMode}
          onClose={() => {
            setSelectedMatch(null)
            setEditBetMode(false)
            queryClient.invalidateQueries({ queryKey: ['matches'] })
          }}
        />
      )}
    </div>
  )
}

// =========================================================================
// TARJETA DE PARTIDO
// =========================================================================
function MatchBetCard({ match, onOpenBet }) {
  const { user } = useAuthStore()
  const canBetLate = user?.rol === 'superadmin' || parseInt(user?.permitir_apostar_iniciados) === 1
  const isClosed = (match.estado !== 'programado' && match.estado !== 'en_juego') || 
                   (new Date() >= parseBoliviaDate(match.fecha_partido) && !canBetLate) ||
                   (match.estado === 'en_juego' && !canBetLate)
  
  return (
    <Card className="p-5 flex flex-col justify-between border border-border-subtle hover:border-border-default transition-all duration-300 relative overflow-hidden bg-surface-card/60">
      {/* Encabezado tarjeta */}
      <div className="flex items-center justify-between mb-4 text-xs text-text-muted">
        <span className="font-mono">
          {match.fase === 'grupos' ? `Grupo ${match.grupo_nombre}` : match.fase.toUpperCase()}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {parseBoliviaDate(match.fecha_partido).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>

      {/* Equipos */}
      <div className="flex items-center justify-center gap-4 my-2 text-center">
        <div className="flex-1 space-y-1.5">
          <Flag code={match.equipo_local_bandera} className="w-10 h-6.5 mx-auto object-cover rounded shadow-sm" />
          <p className="font-display font-semibold text-sm line-clamp-1">{match.equipo_local_nombre}</p>
        </div>
        <div className="text-center font-display font-bold text-xs text-text-muted py-1 bg-navy-dark/40 px-2 rounded-lg border border-border-subtle">
          VS
        </div>
        <div className="flex-1 space-y-1.5">
          <Flag code={match.equipo_visitante_bandera} className="w-10 h-6.5 mx-auto object-cover rounded shadow-sm" />
          <p className="font-display font-semibold text-sm line-clamp-1">{match.equipo_visitante_nombre}</p>
        </div>
      </div>

      {/* Estado del partido y marcador real si finalizó */}
      <div className="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between gap-2">
        {match.estado === 'finalizado' ? (
          <div className="text-xs text-text-secondary flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span>Resultado: <strong>{match.goles_local} - {match.goles_visitante}</strong></span>
          </div>
        ) : match.estado === 'en_juego' ? (
          <Badge variant="warning" size="sm" icon={Play}>En Juego</Badge>
        ) : isClosed ? (
          <Badge variant="default" size="sm" icon={Clock}>Apuestas Cerradas</Badge>
        ) : (
          <Badge variant="success" size="sm">Abierto</Badge>
        )}

        <Button
          size="xs"
          variant={isClosed ? 'outline' : 'primary'}
          onClick={onOpenBet}
        >
          {isClosed ? 'Ver Pozo' : '🪙 Apostar'}
        </Button>
      </div>
    </Card>
  )
}

// =========================================================================
// MODAL DE APUESTA EN PARTIDO
// =========================================================================
function BetMatchModal({ match, isOpen, isEditingInit = false, onClose }) {
  const containerRef = useRef(null)
  const toast = useToast()
  const queryClient = useQueryClient()
  const { credits, fetchBalance } = useBetStore()
  const { user } = useAuthStore()
  const [activeMarket, setActiveMarket] = useState('1x2')
  const [isEditing, setIsEditing] = useState(isEditingInit)

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })
  const multiplicador = Number(settings?.multiplicador_goles_exacto || '3')


  useEffect(() => {
    setIsEditing(isEditingInit)
  }, [isEditingInit])
  
  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [isOpen])
  
  // State for 1X2 market
  const [golesLocal1x2, setGolesLocal1x2] = useState('0')
  const [golesVisitante1x2, setGolesVisitante1x2] = useState('0')
  const [monto1x2, setMonto1x2] = useState('')

  // State for Classification market
  const [clasificaSeleccion, setClasificaSeleccion] = useState('')
  const [montoClasifica, setMontoClasifica] = useState('')

  // Aliases for active market state
  const golesLocal = golesLocal1x2
  const setGolesLocal = setGolesLocal1x2
  const golesVisitante = golesVisitante1x2
  const setGolesVisitante = setGolesVisitante1x2

  const monto = activeMarket === '1x2' ? monto1x2 : montoClasifica
  const setMonto = (val) => {
    if (activeMarket === '1x2') {
      setMonto1x2(val)
    } else {
      setMontoClasifica(val)
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Cargar datos en vivo del pozo de este partido
  const { data: poolData, isLoading: isLoadingPool } = useQuery({
    queryKey: ['matchPool', match.id],
    queryFn: async () => {
      const response = await client.get(`/bets/match/${match.id}`)
      return response.data?.datos
    },
    enabled: isOpen
  })

  // Obtener el pozo del mercado activo
  const pozoActive = poolData?.pozos?.[activeMarket] || {
    monto_total: 0.00,
    monto_local: 0.00,
    monto_visitante: 0.00,
    monto_empate: 0.00,
    cuota_local: 1.00,
    cuota_visitante: 1.00,
    cuota_empate: 1.00,
    estado: 'abierto'
  }
  const miApuestaActive = poolData?.apuestas_usuario?.[activeMarket]

  const miApuesta1x2 = poolData?.apuestas_usuario?.['1x2']
  const miApuestaClasifica = poolData?.apuestas_usuario?.['clasifica']

  // Sincronizar inputs del mercado 1X2 al cargar/cambiar miApuesta1x2
  useEffect(() => {
    if (miApuesta1x2) {
      setGolesLocal1x2(String(miApuesta1x2.goles_local_predichos ?? '0'))
      setGolesVisitante1x2(String(miApuesta1x2.goles_visitante_predichos ?? '0'))
      setMonto1x2(String(miApuesta1x2.monto))
    } else {
      setGolesLocal1x2('0')
      setGolesVisitante1x2('0')
      setMonto1x2('')
    }
  }, [miApuesta1x2, isOpen])

  // Sincronizar inputs del mercado Clasifica al cargar/cambiar miApuestaClasifica
  useEffect(() => {
    if (miApuestaClasifica) {
      setClasificaSeleccion(miApuestaClasifica.seleccion ?? '')
      setMontoClasifica(String(miApuestaClasifica.monto))
    } else {
      setClasificaSeleccion('')
      setMontoClasifica('')
    }
  }, [miApuestaClasifica, isOpen])

  const calculatedSelection = useMemo(() => {
    if (activeMarket === '1x2') {
      const gl = parseInt(golesLocal1x2) || 0
      const gv = parseInt(golesVisitante1x2) || 0
      if (gl > gv) return 'local'
      if (gv > gl) return 'visitante'
      return 'empate'
    } else {
      return clasificaSeleccion
    }
  }, [activeMarket, golesLocal1x2, golesVisitante1x2, clasificaSeleccion])

  const calculatedGolesPredichos = useMemo(() => {
    if (activeMarket === '1x2') {
      const gl = parseInt(golesLocal1x2) || 0
      const gv = parseInt(golesVisitante1x2) || 0
      return Math.max(gl, gv)
    }
    return 0
  }, [activeMarket, golesLocal1x2, golesVisitante1x2])

  const calculatedCuota = useMemo(() => {
    if (calculatedSelection === 'local') return pozoActive.cuota_local
    if (calculatedSelection === 'visitante') return pozoActive.cuota_visitante
    return pozoActive.cuota_empate
  }, [calculatedSelection, pozoActive])

  const canBetLate = user?.rol === 'superadmin' || parseInt(user?.permitir_apostar_iniciados) === 1
  const isClosedActive = (match.estado !== 'programado' && match.estado !== 'en_juego') || 
                         (new Date() >= parseBoliviaDate(match.fecha_partido) && !canBetLate) || 
                         (pozoActive.estado === 'liquidado') ||
                         (pozoActive.estado !== 'abierto' && !canBetLate) ||
                         (match.estado === 'en_juego' && !canBetLate)

  const totalApostado = Number(pozoActive.monto_total)
  const percentLocal = totalApostado > 0 ? (Number(pozoActive.monto_local) / totalApostado) * 100 : 0
  const percentVisitante = totalApostado > 0 ? (Number(pozoActive.monto_visitante) / totalApostado) * 100 : 0
  const percentEmpate = totalApostado > 0 ? (Number(pozoActive.monto_empate) / totalApostado) * 100 : 0

  const handlePlaceBet = async (e) => {
    e.preventDefault()
    setErrorMsg('')

    if (activeMarket === 'clasifica' && !clasificaSeleccion) {
      setErrorMsg('Por favor selecciona qué equipo clasificará de ronda.')
      return
    }
    
    const valMonto = parseFloat(monto)
    if (isNaN(valMonto) || valMonto < 1) {
      setErrorMsg('El monto mínimo para apostar es 1 crédito.')
      return
    }

    if (miApuestaActive && isEditing) {
      const diff = valMonto - Number(miApuestaActive.monto)
      if (diff > 0 && diff > credits) {
        setErrorMsg(`Saldo insuficiente para aumentar el monto. Necesitas ${diff.toFixed(2)} Bs adicionales. Tu saldo es ${credits.toFixed(2)} Bs.`)
        return
      }
    } else if (!miApuestaActive && valMonto > credits) {
      setErrorMsg(`Saldo insuficiente. Tu saldo es ${credits.toFixed(2)} Bs.`)
      return
    }

    setIsSubmitting(true)
    try {
      if (miApuestaActive && isEditing) {
        const payload = {
          seleccion: calculatedSelection,
          monto: valMonto
        }
        if (activeMarket === '1x2') {
          payload.goles_predichos = calculatedGolesPredichos
          payload.goles_local_predichos = parseInt(golesLocal) || 0
          payload.goles_visitante_predichos = parseInt(golesVisitante) || 0
        }
        await client.put(`/bets/${miApuestaActive.id}`, payload)
        toast.success('¡Apuesta modificada con éxito!')
        setIsEditing(false)
      } else {
        const payload = {
          partido_id: match.id,
          seleccion: calculatedSelection,
          monto: valMonto,
          mercado: activeMarket
        }
        if (activeMarket === '1x2') {
          payload.goles_predichos = calculatedGolesPredichos
          payload.goles_local_predichos = parseInt(golesLocal) || 0
          payload.goles_visitante_predichos = parseInt(golesVisitante) || 0
        }
        await client.post('/bets', payload)
        toast.success('¡Apuesta colocada con éxito!')
      }
      fetchBalance()
      queryClient.invalidateQueries({ queryKey: ['matchPool', match.id] })
      queryClient.invalidateQueries({ queryKey: ['userBets'] })
    } catch (err) {
      setErrorMsg(err.response?.data?.mensaje || 'Error al procesar la apuesta.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
        />

        <motion.div
          ref={containerRef}
          onScroll={(e) => { e.currentTarget.scrollTop = 0 }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white dark:bg-navy-dark border border-border-default shadow-2xl z-10 flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-navy-dark/40">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-cyan" />
              <h2 className="font-display font-bold text-base text-text-primary">Pozo de Apuestas</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full bg-navy-light/50 hover:bg-cyan hover:text-navy-dark transition-all text-text-primary border border-border-subtle flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Selector de mercados para partidos de eliminación directa */}
          {match.fase !== 'grupos' && (
            <div className="flex border-b border-border-subtle bg-navy-dark/20 p-2 gap-2">
              <button
                type="button"
                onClick={() => { setActiveMarket('1x2'); setErrorMsg(''); }}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all border ${
                  activeMarket === '1x2'
                    ? 'bg-cyan text-navy-dark border-cyan font-bold shadow-md'
                    : 'bg-transparent text-text-secondary border-transparent hover:text-text-primary hover:bg-navy-light/35'
                }`}
              >
                ⏱️ 90 Minutos (1X2)
              </button>
              <button
                type="button"
                onClick={() => { setActiveMarket('clasifica'); setErrorMsg(''); }}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all border ${
                  activeMarket === 'clasifica'
                    ? 'bg-cyan text-navy-dark border-cyan font-bold shadow-md'
                    : 'bg-transparent text-text-secondary border-transparent hover:text-text-primary hover:bg-navy-light/35'
                }`}
              >
                🏆 ¿Quién Clasifica?
              </button>
            </div>
          )}

          {isLoadingPool ? (
            <div className="p-8 space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <form onSubmit={handlePlaceBet} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-5">
              
              {/* Marcador Interactivo (Scoreboard TV) en la parte superior */}
              {activeMarket === '1x2' ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="font-display font-semibold text-sm">Crear Ticket de Apuesta</h3>
                    <span className="text-[10px] text-text-muted font-medium uppercase tracking-wider">Predicción de Goles</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-3 px-4 bg-navy-dark/40 rounded-xl border border-border-subtle shadow-inner">
                    {/* Local Team */}
                    <div className="flex-1 flex items-center justify-end gap-2.5 min-w-0">
                      <span className="font-display font-semibold text-xs sm:text-sm text-text-primary truncate">{match.equipo_local_nombre}</span>
                      <Flag code={match.equipo_local_bandera} className="w-7.5 h-5 shrink-0 object-cover rounded shadow-sm" />
                    </div>

                    {/* Score input local */}
                    <input
                      type="number"
                      min="0"
                      max="20"
                      required
                      disabled={!isEditing && (!!miApuestaActive || isClosedActive)}
                      value={golesLocal}
                      onChange={(e) => setGolesLocal(e.target.value)}
                      className="w-12 h-10 bg-navy-light/35 border border-border-subtle focus:border-cyan text-center font-mono font-bold text-lg text-text-primary rounded-xl focus:outline-none disabled:opacity-50"
                    />

                    {/* VS separator */}
                    <span className="text-text-muted font-display font-bold text-xs uppercase tracking-wider">VS</span>

                    {/* Score input visitor */}
                    <input
                      type="number"
                      min="0"
                      max="20"
                      required
                      disabled={!isEditing && (!!miApuestaActive || isClosedActive)}
                      value={golesVisitante}
                      onChange={(e) => setGolesVisitante(e.target.value)}
                      className="w-12 h-10 bg-navy-light/35 border border-border-subtle focus:border-cyan text-center font-mono font-bold text-lg text-text-primary rounded-xl focus:outline-none disabled:opacity-50"
                    />

                    {/* Visitor Team */}
                    <div className="flex-1 flex items-center justify-start gap-2.5 min-w-0">
                      <Flag code={match.equipo_visitante_bandera} className="w-7.5 h-5 shrink-0 object-cover rounded shadow-sm" />
                      <span className="font-display font-semibold text-xs sm:text-sm text-text-primary truncate">{match.equipo_visitante_nombre}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="font-display font-semibold text-sm">Selecciona el equipo que avanza</h3>
                    <span className="text-[10px] text-text-muted font-medium uppercase tracking-wider">Mercado Clasificación</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 select-none">
                    {/* Equipo Local */}
                    <div
                      onClick={() => !isClosedActive && (!miApuestaActive || isEditing) && setClasificaSeleccion('local')}
                      className={`glass p-4 rounded-2xl border transition-all flex flex-col items-center gap-2 cursor-pointer ${
                        clasificaSeleccion === 'local'
                          ? 'border-2 border-cyan bg-cyan/20 scale-105 shadow-[0_0_15px_rgba(6,182,212,0.3)] ring-1 ring-cyan/30 z-10'
                          : clasificaSeleccion === 'visitante'
                            ? 'border-border-subtle bg-navy-dark/10 opacity-40 scale-95'
                            : 'border-border-subtle hover:border-text-secondary bg-navy-dark/20 hover:scale-102'
                      } ${(isClosedActive && !isEditing) && 'opacity-60 cursor-not-allowed'}`}
                    >
                      <Flag code={match.equipo_local_bandera} className="w-12 h-8 object-cover rounded shadow-md" />
                      <span className="font-display font-bold text-sm text-text-primary text-center leading-tight">
                        {match.equipo_local_nombre}
                      </span>
                      <span className="text-[10px] text-text-muted font-semibold uppercase">Local</span>
                    </div>

                    {/* Equipo Visitante */}
                    <div
                      onClick={() => !isClosedActive && (!miApuestaActive || isEditing) && setClasificaSeleccion('visitante')}
                      className={`glass p-4 rounded-2xl border transition-all flex flex-col items-center gap-2 cursor-pointer ${
                        clasificaSeleccion === 'visitante'
                          ? 'border-2 border-cyan bg-cyan/20 scale-105 shadow-[0_0_15px_rgba(6,182,212,0.3)] ring-1 ring-cyan/30 z-10'
                          : clasificaSeleccion === 'local'
                            ? 'border-border-subtle bg-navy-dark/10 opacity-40 scale-95'
                            : 'border-border-subtle hover:border-text-secondary bg-navy-dark/20 hover:scale-102'
                      } ${(isClosedActive && !isEditing) && 'opacity-60 cursor-not-allowed'}`}
                    >
                      <Flag code={match.equipo_visitante_bandera} className="w-12 h-8 object-cover rounded shadow-md" />
                      <span className="font-display font-bold text-sm text-text-primary text-center leading-tight">
                        {match.equipo_visitante_nombre}
                      </span>
                      <span className="text-[10px] text-text-muted font-semibold uppercase">Visitante</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Estadísticas de Pozo */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-text-secondary">Pozo Acumulado Neto:</span>
                  <span className="text-sm font-display font-bold text-cyan">
                    {(Number(pozoActive.monto_total) * (1 - Number(pozoActive.comision) / 100)).toFixed(2)} Bs
                  </span>
                </div>
                
                {/* Barras de distribución */}
                <div className="space-y-2.5">
                  {/* Local */}
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">{match.equipo_local_nombre} (L)</span>
                      <span className="font-mono text-text-primary">
                        {percentLocal.toFixed(0)}% ({Number(pozoActive.monto_local).toFixed(0)} Bs) - <strong>Cuota: {pozoActive.cuota_local}x</strong>
                      </span>
                    </div>
                    <div className="h-2 w-full bg-navy-dark/60 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan transition-all" style={{ width: `${percentLocal}%` }} />
                    </div>
                  </div>

                  {/* Empate (solo para 1x2) */}
                  {activeMarket === '1x2' && (
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Empate (X)</span>
                        <span className="font-mono text-text-primary">
                          {percentEmpate.toFixed(0)}% ({Number(pozoActive.monto_empate).toFixed(0)} Bs) - <strong>Cuota: {pozoActive.cuota_empate}x</strong>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-navy-dark/60 rounded-full overflow-hidden">
                        <div className="h-full bg-border-default transition-all" style={{ width: `${percentEmpate}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Visitante */}
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">{match.equipo_visitante_nombre} (V)</span>
                      <span className="font-mono text-text-primary">
                        {percentVisitante.toFixed(0)}% ({Number(pozoActive.monto_visitante).toFixed(0)} Bs) - <strong>Cuota: {pozoActive.cuota_visitante}x</strong>
                      </span>
                    </div>
                    <div className="h-2 w-full bg-navy-dark/60 rounded-full overflow-hidden">
                      <div className="h-full bg-blue transition-all" style={{ width: `${percentVisitante}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              {(miApuestaActive && !isEditing) ? (
                /* Apuesta Ya Realizada */
                <div className="p-4 rounded-xl border border-success/30 bg-success/5 space-y-3 text-sm">
                  <h3 className="font-display font-bold text-success flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Apuesta Registrada
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-text-muted">Tu Selección:</span>
                      <p className="font-semibold capitalize text-text-primary mt-0.5">
                        {miApuestaActive.seleccion === 'local' ? match.equipo_local_nombre : miApuestaActive.seleccion === 'visitante' ? match.equipo_visitante_nombre : 'Empate'}
                      </p>
                    </div>
                    {activeMarket === '1x2' && (
                      <div>
                        <span className="text-text-muted">Marcador Predicho:</span>
                        <p className="font-semibold text-text-primary mt-0.5 font-mono">
                          {miApuestaActive.goles_local_predichos} - {miApuestaActive.goles_visitante_predichos}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-text-muted">Monto Apostado:</span>
                      <p className="font-semibold text-text-primary mt-0.5">{Number(miApuestaActive.monto).toFixed(2)} Bs</p>
                    </div>
                    <div>
                      <span className="text-text-muted">Estado:</span>
                      <p className="mt-0.5 font-bold">
                        {miApuestaActive.estado === 'pendiente' && <span className="text-orange-400">Pendiente</span>}
                        {miApuestaActive.estado === 'ganada' && (
                          <span className={Number(miApuestaActive.premio_pagado) > Number(miApuestaActive.monto) ? "text-success" : Number(miApuestaActive.premio_pagado) === Number(miApuestaActive.monto) ? "text-text-secondary" : "text-error"}>
                            Ganada ({Number(miApuestaActive.premio_pagado).toFixed(2)} Bs | {Number(miApuestaActive.premio_pagado) - Number(miApuestaActive.monto) >= 0 ? '+' : ''}{(Number(miApuestaActive.premio_pagado) - Number(miApuestaActive.monto)).toFixed(2)} Bs netos)
                          </span>
                        )}
                        {miApuestaActive.estado === 'perdida' && <span className="text-error">Perdida</span>}
                        {miApuestaActive.estado === 'devuelta' && <span className="text-text-muted">Reembolsada ({Number(miApuestaActive.premio_pagado).toFixed(2)} Bs)</span>}
                      </p>
                    </div>
                  </div>
                  {user?.permitir_editar_apuestas === 1 && match.estado !== 'finalizado' && pozoActive.estado !== 'liquidado' && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full mt-2"
                      onClick={() => setIsEditing(true)}
                    >
                      ✏️ Modificar esta Apuesta
                    </Button>
                  )}
                </div>
              ) : (isClosedActive && !isEditing) ? (
                /* Cerrado sin apuesta */
                <div className="bg-navy-dark/40 border border-border-subtle p-4 rounded-xl text-center text-xs text-text-muted flex flex-col items-center justify-center gap-1">
                  <AlertTriangle className="w-6 h-6 text-warning" />
                  <p className="font-semibold">El pozo está cerrado para nuevas apuestas.</p>
                  <p>El partido ya ha comenzado o se encuentra liquidado.</p>
                </div>
              ) : (
                /* Formulario para Apostar - Campos restantes */
                <div className="space-y-4">
                  {/* Resumen de Selección y Cuota */}
                  <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-navy-dark/45 border border-border-subtle text-xs">
                    <div className="space-y-0.5">
                      <span className="text-text-muted text-[10px] uppercase tracking-wider">Tu Selección</span>
                      <p className="font-semibold text-text-primary capitalize truncate">
                        {calculatedSelection === 'local' 
                          ? `${match.equipo_local_nombre} (${activeMarket === '1x2' ? 'L' : 'Clasifica'})` 
                          : calculatedSelection === 'visitante' 
                            ? `${match.equipo_visitante_nombre} (${activeMarket === '1x2' ? 'V' : 'Clasifica'})` 
                            : activeMarket === '1x2' ? 'Empate (X)' : 'Selecciona un equipo'}
                      </p>
                    </div>
                    <div className="space-y-0.5 text-right">
                      <span className="text-text-muted text-[10px] uppercase tracking-wider">Cuota Estimada</span>
                      <p className={`font-mono font-bold text-sm ${
                        calculatedSelection === 'local' 
                          ? 'text-cyan' 
                          : calculatedSelection === 'visitante' 
                            ? 'text-blue-400' 
                            : 'text-text-primary'
                      }`}>
                        {calculatedCuota}x
                      </p>
                    </div>
                  </div>

                  {/* Monto */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-text-secondary">
                      Monto a Apostar (Bs):
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      disabled={!!miApuestaActive && !isEditing}
                      value={monto}
                      onChange={(e) => setMonto(e.target.value)}
                      placeholder="Ej. 10"
                      className="w-full bg-navy-dark/50 border border-border-subtle rounded-xl px-3 py-2 text-text-primary focus:outline-none focus:border-cyan text-sm disabled:opacity-50"
                    />
                  </div>

                  {activeMarket === '1x2' ? (
                    <div className="bg-cyan/5 border border-cyan/15 rounded-xl p-2.5 text-[11px] text-cyan flex gap-1.5 items-start">
                      <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>
                        <strong>Bono de goles:</strong> Si tu equipo gana y aciertas el marcador exacto de ambos equipos, obtienes <strong>{multiplicador} participaciones</strong> del pozo en lugar de 1, multiplicando tu premio.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-cyan/5 border border-cyan/15 rounded-xl p-2.5 text-[11px] text-cyan flex gap-1.5 items-start">
                      <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>
                        <strong>Clasificación:</strong> Apuestas por el equipo que avanza a la siguiente ronda del torneo. Válido incluyendo prórroga (alargue) y tanda de penales si fuera necesario. No aplica bono de goles.
                      </p>
                    </div>
                  )}

                  {errorMsg && (
                    <div className="bg-error/10 border border-error/25 text-error rounded-xl p-3 text-xs flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 py-2.5"
                      onClick={onClose}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 py-2.5"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-1.5 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {miApuestaActive ? 'Guardando...' : 'Apostando...'}
                        </span>
                      ) : (
                        miApuestaActive ? 'Guardar Cambios' : 'Confirmar Apuesta'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

// =========================================================================
// HISTORIAL DE APUESTAS DEL USUARIO
// =========================================================================
function UserBetsHistory({ bets, isLoading, user, onEditBet }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
    )
  }

  if (bets.length === 0) {
    return (
      <Card className="p-8 text-center text-text-muted">
        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Aún no has realizado ninguna apuesta por equipos.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {bets.map((bet) => {
        const isWinner = bet.estado === 'ganada'
        const isPending = bet.estado === 'pendiente'
        const isRefund = bet.estado === 'devuelta'
        
        return (
          <Card key={bet.id} className="p-4 border border-border-subtle bg-surface-card/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2 flex-1">
              {/* Meta info */}
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span className="font-mono uppercase bg-navy-dark px-1.5 py-0.5 rounded border border-border-subtle">
                  Partido #{bet.partido_id}
                </span>
                <span>
                  {parseBoliviaDate(bet.fecha_partido).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>

              {/* Match Details */}
              <div className="flex items-center gap-2.5">
                <Flag code={bet.equipo_local_bandera} className="w-6 h-4 object-cover rounded shadow-sm" />
                <span className="font-display font-semibold text-sm text-text-primary">{bet.equipo_local_nombre}</span>
                <span className="text-text-muted text-xs">
                  {bet.goles_local !== null ? `${bet.goles_local} - ${bet.goles_visitante}` : 'vs'}
                </span>
                <span className="font-display font-semibold text-sm text-text-primary">{bet.equipo_visitante_nombre}</span>
                <Flag code={bet.equipo_visitante_bandera} className="w-6 h-4 object-cover rounded shadow-sm" />
              </div>

              {/* Bet Details */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary pt-0.5 font-sans">
                <p>
                  Mercado: <strong className="text-text-primary">
                    {bet.mercado === 'clasifica' ? '🏆 Clasificación' : '⏱️ 90 Minutos'}
                  </strong>
                </p>
                <p>
                  Selección: <strong className="capitalize text-text-primary">
                    {bet.seleccion === 'local' ? bet.equipo_local_nombre : bet.seleccion === 'visitante' ? bet.equipo_visitante_nombre : 'Empate'}
                  </strong>
                </p>
                {bet.mercado !== 'clasifica' && (
                  <p>Marcador Predicho: <strong className="text-text-primary font-mono">{bet.goles_local_predichos} - {bet.goles_visitante_predichos}</strong></p>
                )}
                <p>Importe: <strong className="text-cyan font-semibold">{Number(bet.monto).toFixed(2)} Bs</strong></p>
              </div>
            </div>

            {/* Status & Payout */}
            <div className="flex flex-col items-start sm:items-end justify-center gap-1 sm:border-l border-border-subtle sm:pl-6 shrink-0">
              <span className="text-xs text-text-muted">Estado de Ticket</span>
              {isPending && (
                <div className="flex flex-col gap-1.5 items-start sm:items-end">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    Pendiente
                  </span>
                  {user?.permitir_editar_apuestas === 1 && bet.partido_estado !== 'finalizado' && (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => onEditBet(bet)}
                    >
                      ✏️ Modificar Apuesta
                    </Button>
                  )}
                </div>
              )}
              {isWinner && (
                <div className="space-y-0.5 text-right">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold inline-block border ${
                    Number(bet.premio_pagado) > Number(bet.monto)
                      ? 'bg-success/10 text-success border-success/20'
                      : Number(bet.premio_pagado) === Number(bet.monto)
                      ? 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                      : 'bg-error/10 text-error border border-error/20'
                  }`}>
                    {Number(bet.premio_pagado) > Number(bet.monto) ? 'Ganador' : Number(bet.premio_pagado) === Number(bet.monto) ? 'Reembolso' : 'Pérdida Parcial'}
                  </span>
                  <p className={`text-sm font-display font-bold mt-0.5 ${
                    Number(bet.premio_pagado) > Number(bet.monto)
                      ? 'text-success'
                      : Number(bet.premio_pagado) === Number(bet.monto)
                      ? 'text-text-secondary'
                      : 'text-error'
                  }`}>
                    {Number(bet.premio_pagado) > Number(bet.monto) ? '+' : ''}
                    {Number(bet.premio_pagado).toFixed(2)} Bs
                    {Number(bet.premio_pagado) !== Number(bet.monto) && (
                      <span className="text-[10px] font-normal block opacity-85">
                        ({(Number(bet.premio_pagado) - Number(bet.monto)) >= 0 ? '+' : ''}
                        {(Number(bet.premio_pagado) - Number(bet.monto)).toFixed(2)} Bs netos)
                      </span>
                    )}
                  </p>
                </div>
              )}
              {bet.estado === 'perdida' && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-error/10 text-error border border-error/20">
                  No Acertado
                </span>
              )}
              {isRefund && (
                <div className="space-y-0.5 text-right">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-border-default/10 text-text-secondary border border-border-default inline-block">
                    Reembolsado
                  </span>
                  <p className="text-sm font-display font-bold text-text-secondary">
                    {Number(bet.premio_pagado).toFixed(2)} Bs
                  </p>
                </div>
              )}
            </div>
          </Card>
        )
      })}
    </div>
  )
}
